import type { BaseFields } from "./base";
import type { TicketWithId } from "./ticket";

export type MultilingualDescription = {
  [language: string]: string;
};

export type CefrLevel = "Pre-A1" | "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

export type CategoryWithDescription = {
  score: number;
  cefrLevel: CefrLevel;
  label?: CefrLevel;
  description: MultilingualDescription;
};

export type CefrResults = {
  accuracy: CategoryWithDescription;
  coherence: CategoryWithDescription;
  fluency: CategoryWithDescription;
  interaction: CategoryWithDescription;
  overall: CategoryWithDescription;
  phonology: CategoryWithDescription;
  range: CategoryWithDescription;
};

export type AssessmentData = {
  cefrResults: CefrResults;
  feedback: {
    strength: MultilingualDescription;
    weakness: MultilingualDescription;
  };
  levelDescription: MultilingualDescription;
  transcriptFileId: string | null;
};

export type DialogAssessment = {
  id: string;
  dialogId: string | null;
  createdAt: number;
  assessmentData: AssessmentData;
};

export type ReviewCardData = {
  targetSystemUtteranceEnglishText: string;
  targetSystemUtteranceJapaneseText: string;
  targetUserUtteranceEnglishText: string;
  targetSystemUtteranceEnglishAudio: string;
  targetUserUtteranceEnglishAudio: string | null;
  feedbackEnglishText: string;
  feedbackJapaneseText: string;
  adviceEnglishText: string;
  adviceJapaneseText: string;
  feedbackEnglishAudio: string;
  feedbackEnglishAudioSlow: string;
  wordOrderQuiz: string;
  userScrambleInput: string;
};

export type DialogReviewCard = BaseFields & {
  id: string;
  practicedAt: number | null;
  reviewCardData: ReviewCardData;
  reviewId: string;
};

export type DialogReview = BaseFields & {
  id: string;
  campaignId: string;
  dialogId: string | null;
  isFinished: boolean;
  packageId: string;
  reviewCards: DialogReviewCard[];
  reviewedAt: number | null;
  userId: string | null;
};

export const CAN_DO_ACHIEVEMENTS = ["success", "partial", "fail"] as const;

export type CanDoAchievement = (typeof CAN_DO_ACHIEVEMENTS)[number];

export const isCanDoAchievement = (value: unknown): value is CanDoAchievement =>
  (CAN_DO_ACHIEVEMENTS as readonly unknown[]).includes(value);

export type DialogCanDoItem = {
  canDo: {
    en: string;
    ja: string;
  };
  isAchieved: boolean;
  achievement?: string;
  sortOrder: number;
};

export type DialogCanDo = BaseFields & {
  id: string;
  success: boolean;
  gnowsisVersion: {
    systemVersion: string;
    llmModel: string;
    promptVersion: string;
  };
  dialogId: string;
  userId: string;
  result: {
    canDos: DialogCanDoItem[];
    overallFeedback: {
      en: string;
      ja: string;
    };
  };
  errorMessage: string;
};

export type GnowsisGroups = BaseFields & {
  id: string;
  name: string;
  resultTableName: string;
};

export type Dialog = BaseFields & {
  agentGroupId: string | null;
  agentUrl: string | null;
  campaignIdLegacy: string;
  canceledAt: number | null;
  customerIdLegacy: string;
  customerCode?: string | null;
  endedAt: number | null;
  errorCode: string | null;
  gnowsisGroupIds: string[];
  gnowsisGroupTypes: string[];
  id: string;
  language: string;
  learnerId: string;
  readyAt: number | null;
  reservedAt: number;
  scenarioId: string | null;
  startedAt: number | null;
  ticketId: string;
  userAgent: string | null;
  resultIdLegacy: string | null;
  hasVideo: boolean;
};

export type DialogCampaign = {
  id: string;
  name: string;
  code: string;
};

export type DialogPackage = {
  id: string;
  name: string;
  code: string;
};

export type DialogWithRelations = Dialog & {
  assessment?: DialogAssessment | null;
  review?: DialogReview | null;
  canDo?: DialogCanDo | null;
  campaign: DialogCampaign | null;
  package: DialogPackage | null;
  ticket?: TicketWithId | null;
};
